import React from 'react';
import ReactFlow, { MiniMap, Controls, Background, Position, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import '../../App.css';

const themeStyles = {
  generalNode: {
    background: '#ffffff',
    border: '1px solid #4a148c', // Purple
    borderRadius: '8px',
    padding: '10px 15px',
    fontSize: '12px',
    color: '#333333',
    width: 180,
    textAlign: 'center',
  },
  populationNode: { // For initial population and final solution
    background: '#e1f5fe', // Light Blue
    border: '2px solid #0277bd', // Darker Blue
    borderRadius: '8px',
    padding: '15px 20px',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#0277bd',
    width: 220,
    textAlign: 'center',
  },
  variationNode: { // For individual code variations
    background: '#fce4ec', // Light Pink
    border: '1px solid #d81b60', // Pink
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '11px',
    color: '#333333',
    width: 150,
    textAlign: 'center',
  },
  processNode: { // For selection, crossover, mutation
    background: '#fffde7', // Light Yellow
    border: '1px solid #fdd835', // Yellow
    borderRadius: '8px',
    padding: '10px 15px',
    fontSize: '12px',
    color: '#333333',
    width: 280, // Increased width
    textAlign: 'center',
  },
  groupNode: {
    backgroundColor: 'rgba(74, 20, 140, 0.05)', // Very light purple
    border: '1px dashed #4a148c', // Purple dashed border
    borderRadius: '10px',
    padding: '20px',
  },
  edgeStyle: {
    stroke: '#4a148c', // Purple
    strokeWidth: 2,
  },
  mutationNode: {
    background: '#e8f5e9', // Light Green
    border: '1px solid #2e7d32', // Green
    borderRadius: '8px',
    padding: '10px 15px',
    fontSize: '12px',
    color: '#333333',
    width: 250,
    textAlign: 'center',
  },
  crossoverNode: {
    background: '#e3f2fd', // Light Blue
    border: '1px solid #1565c0', // Blue
    borderRadius: '8px',
    padding: '10px 15px',
    fontSize: '12px',
    color: '#333333',
    width: 250,
    textAlign: 'center',
  },
  userInputNode: {
    background: '#f3e5f5', // Light Purple
    border: '2px solid #7b1fa2', // Purple
    borderRadius: '8px',
    padding: '12px 18px',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#7b1fa2',
    width: 220,
    textAlign: 'center',
  },
};

const initialNodes = [
  // User Input
  {
    id: 'user-input',
    data: { label: '🧑‍💻 User Prompt + Initial Code' },
    position: { x: 50, y: 300 },
    sourcePosition: Position.Right,
    style: themeStyles.userInputNode,
  },
  
  // Initial Scoring & Goal Generation
  {
    id: 'goal-generation',
    data: { label: '🎯 Goal Generation & Initial Scoring\n(Speed, Memory, etc.)' },
    position: { x: 350, y: 300 }, 
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.processNode,
  },

  // 0. Initial Population
  {
    id: 'initial-pop',
    data: { label: '🧬 Initial Population' },
    position: { x: 700, y: 300 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.populationNode,
  },

  // Generation 1 Group
  {
    id: 'group-gen1',
    type: 'group',
    data: { label: 'Generation 1 (LLM/Agent-Generated Variations)' },
    position: { x: 1000, y: 150 },
    style: { ...themeStyles.groupNode, width: 200, height: 350 },
  },
  ...Array.from({ length: 5 }, (_, i) => ({
    id: `gen1-var${i + 1}`,
    data: { label: `Variation 1.${i + 1}` },
    position: { x: 25, y: 50 + i * 60 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    parentNode: 'group-gen1',
    extent: 'parent',
    style: themeStyles.variationNode,
  })),

  // Evolutionary Processes 1
  {
    id: 'eval1',
    data: { label: '🧐 Initial Fitness Evaluation' },
    position: { x: 1300, y: 200 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.processNode,
  },
  {
    id: 'llm-agent-scoring1',
    data: { label: '🤖 LLM/Agent Scoring\n(Based on performance metrics)' },
    position: { x: 1300, y: 300 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.processNode, 
  },
  {
    id: 'human-scoring1',
    data: { label: '🧑‍💻 Human Feedback\n(Execution results & direct input)' },
    position: { x: 1300, y: 400 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.processNode, 
  },
  {
    id: 'select1',
    data: { label: '👍 Selection of Best Variations' },
    position: { x: 1650, y: 300 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.processNode,
  },

  // Explicit Mutation & Crossover Nodes
  {
    id: 'mutation1',
    data: { label: '🧪 Mutation\n(LLM applies targeted goal & adds context)' },
    position: { x: 1950, y: 250 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.mutationNode,
  },
  {
    id: 'crossover1',
    data: { label: '🧬 Crossover\n(LLM combines features from best variations)' },
    position: { x: 1950, y: 350 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.crossoverNode,
  },
  
  // Context & LLM Processing
  {
    id: 'context-search',
    data: { label: '📚 Context Gathering\n(Search codebase for similar patterns)' },
    position: { x: 1650, y: 100 },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: themeStyles.generalNode,
  },
  {
    id: 'llm-processing',
    data: { label: '🧠 LLM/Agent Processing' },
    position: { x: 1800, y: 100 },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: themeStyles.generalNode,
  },

  // Generation 2 Group
  {
    id: 'group-gen2',
    type: 'group',
    data: { label: 'Generation 2 (Evolved Variations)' },
    position: { x: 2250, y: 150 },
    style: { ...themeStyles.groupNode, width: 200, height: 350 },
  },
  ...Array.from({ length: 5 }, (_, i) => ({
    id: `gen2-var${i + 1}`,
    data: { label: `Variation 2.${i + 1}` },
    position: { x: 25, y: 50 + i * 60 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    parentNode: 'group-gen2',
    extent: 'parent',
    style: themeStyles.variationNode,
  })),
  
  // Evaluation of Generation 2
  {
    id: 'eval2',
    data: { label: '🧐 Fitness Evaluation of Gen 2' },
    position: { x: 2550, y: 250 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.processNode,
  },
  {
    id: 'feedback-loop',
    data: { label: '🔄 Feedback Loop\n(Iterate with improved scores)' },
    position: { x: 2550, y: 350 },
    sourcePosition: Position.Left,
    targetPosition: Position.Right,
    style: themeStyles.processNode,
  },

  // Connector to further generations or solution
  {
    id: 'to-next-gen',
    data: { label: 'Further Generations...' },
    position: { x: 2850, y: 300 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.generalNode,
  },

  // Optimal Solution
  {
    id: 'optimal-solution',
    data: { label: '🏆 Optimal Solution Found' },
    position: { x: 3150, y: 300 },
    targetPosition: Position.Left,
    style: themeStyles.populationNode,
  },

  // Add Score Visualization Node
  {
    id: 'score-visualization',
    data: { label: '📊 Score Visualization\n(Tracking improvements across generations)' },
    position: { x: 1800, y: 500 },
    style: {...themeStyles.generalNode, width: 250},
  },
];

const finalEdges = [
    // User input flow
    { id: 'e-userinput-goalsgen', source: 'user-input', target: 'goal-generation', style: themeStyles.edgeStyle, animated: true, label: 'provides input' },
    { id: 'e-goalsgen-initialpop', source: 'goal-generation', target: 'initial-pop', style: themeStyles.edgeStyle, animated: true },
    
    // Initial population to Gen 1
    { id: 'e-initpop-gen1var1', source: 'initial-pop', target: 'gen1-var1', style: themeStyles.edgeStyle, animated: true, label: "seeds population" },
    
    // Gen 1 to evaluation
    { id: 'e-gen1var5-eval1', source: 'gen1-var5', target: 'eval1', style: themeStyles.edgeStyle, animated: true, label: 'evaluate gen 1' },

    // Evaluation flow
    { id: 'e-eval1-llmscore1', source: 'eval1', target: 'llm-agent-scoring1', style: themeStyles.edgeStyle, animated: true },
    { id: 'e-eval1-humanscore1', source: 'eval1', target: 'human-scoring1', style: themeStyles.edgeStyle, animated: true },
    { id: 'e-llmscore1-select1', source: 'llm-agent-scoring1', target: 'select1', style: themeStyles.edgeStyle, animated: true, label: 'AI scores' },
    { id: 'e-humanscore1-select1', source: 'human-scoring1', target: 'select1', style: themeStyles.edgeStyle, animated: true, label: 'human scores' },
    
    // Selection to mutation & crossover (explicit)  
    { id: 'e-select1-mutation1', source: 'select1', target: 'mutation1', style: themeStyles.edgeStyle, animated: true, label: 'selected candidates' },
    { id: 'e-select1-crossover1', source: 'select1', target: 'crossover1', style: themeStyles.edgeStyle, animated: true, label: 'selected candidates' },
    
    // Context and LLM support
    { id: 'e-context-llm', source: 'context-search', target: 'llm-processing', style: themeStyles.edgeStyle, animated: true, label: 'provides context' },
    { id: 'e-llm-mutation', source: 'llm-processing', target: 'mutation1', style: themeStyles.edgeStyle, animated: true },
    { id: 'e-llm-crossover', source: 'llm-processing', target: 'crossover1', style: themeStyles.edgeStyle, animated: true },
    
    // Mutation & Crossover create Gen 2
    { id: 'e-mutation1-gen2var1', source: 'mutation1', target: 'gen2-var1', style: themeStyles.edgeStyle, animated: true, label: 'creates variations' },
    { id: 'e-crossover1-gen2var3', source: 'crossover1', target: 'gen2-var3', style: themeStyles.edgeStyle, animated: true, label: 'creates variations' },
    
    // Gen 2 evaluation
    { id: 'e-gen2var5-eval2', source: 'gen2-var5', target: 'eval2', style: themeStyles.edgeStyle, animated: true },
    { id: 'e-eval2-feedback', source: 'eval2', target: 'feedback-loop', style: themeStyles.edgeStyle, animated: true },
    { id: 'e-feedback-mutation', source: 'feedback-loop', target: 'mutation1', style: {...themeStyles.edgeStyle, strokeDasharray: '5,5'}, animated: true, label: 'iterative improvement' },
    
    // Path to further generations and solution
    { id: 'e-feedback-nextgen', source: 'feedback-loop', target: 'to-next-gen', style: themeStyles.edgeStyle, animated: true },
    { id: 'e-tonext-optimal', source: 'to-next-gen', target: 'optimal-solution', style: themeStyles.edgeStyle, animated: true, label: 'converges' },
    
    // Score visualization connections
    { id: 'e-eval1-scorevis', source: 'eval1', target: 'score-visualization', style: {...themeStyles.edgeStyle, strokeDasharray: '5,5'}, animated: false },
    { id: 'e-eval2-scorevis', source: 'eval2', target: 'score-visualization', style: {...themeStyles.edgeStyle, strokeDasharray: '5,5'}, animated: false },
    
    // Goal connection to process
    { id: 'e-goalsgen-llmproc', source: 'goal-generation', target: 'llm-processing', style: {...themeStyles.edgeStyle, strokeDasharray: '5,5'}, animated: false, label: 'guides goals' },
];


const ArtemisIntelligenceDiagram = ({ title }) => (
  <ReactFlowProvider>
    <div style={{ height: 'calc(100vh - 60px)', width: '100%', pointerEvents: 'all', background: '#f3e5f5' /* Light lavender */ }}>
      <h2 style={{ textAlign: 'center', color: '#4a148c', paddingTop: '10px', marginBottom: '10px', fontFamily: 'Arial, sans-serif' }}>
        {title || "Artemis Intelligence: AI-Driven Evolutionary Code Optimization"}
      </h2>
      <ReactFlow
        nodes={initialNodes}
        edges={finalEdges}
        fitView
        nodesDraggable={true}
        elementsSelectable={true}
        defaultEdgeOptions={{ animated: true, style: themeStyles.edgeStyle }}
      >
        <MiniMap 
          nodeColor={(node) => {
            if (node.type === 'group') return themeStyles.groupNode.backgroundColor;
            if (node.style && node.style.background) return node.style.background;
            return '#ddd';
          }} 
          nodeStrokeColor={(node) => {
            if (node.type === 'group') return themeStyles.groupNode.border.split(' ')[2];
            if (node.style && node.style.border) return node.style.border.split(' ')[2];
            return '#4a148c';
          }}
          nodeBorderRadius={2}
          zoomable 
          pannable 
        />
        <Controls />
        <Background color="#e0e0e0" gap={20} size={1.5} />
      </ReactFlow>
    </div>
  </ReactFlowProvider>
);

export default ArtemisIntelligenceDiagram;